import { useCallback, useEffect, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import { TOGGLE_LINK_COMMAND, $isLinkNode } from "@lexical/link";
import Icon from "@mdi/react";
import {
  mdiFormatBold,
  mdiFormatItalic,
  mdiFormatUnderline,
  mdiFormatListNumbered,
  mdiFormatListBulleted,
  mdiLink,
} from "@mdi/js";

const ToolbarPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isLink, setIsLink] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));

      const node = selection.anchor.getNode();
      const parent = node.getParent();
      setIsLink($isLinkNode(parent) || $isLinkNode(node));
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, updateToolbar]);

  const handleLink = () => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
    }
  };

  return (
    <div className="lexical-toolbar">
      <div className="toolbar-group">
        <button
          type="button"
          className={`toolbar-btn ${isBold ? "active" : ""}`}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
          title="Bold"
        >
          <Icon path={mdiFormatBold} size={0.83} />
        </button>
        <button
          type="button"
          className={`toolbar-btn ${isItalic ? "active" : ""}`}
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")
          }
          title="Italic"
        >
          <Icon path={mdiFormatItalic} size={0.83} />
        </button>
        <button
          type="button"
          className={`toolbar-btn ${isUnderline ? "active" : ""}`}
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")
          }
          title="Underline"
        >
          <Icon path={mdiFormatUnderline} size={0.83} />
        </button>
      </div>
      <div className="toolbar-group">
        <button
          type="button"
          className="toolbar-btn"
          onClick={() =>
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
          }
          title="Numbered List"
        >
          <Icon path={mdiFormatListNumbered} size={0.83} />
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() =>
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
          }
          title="Bulleted List"
        >
          <Icon path={mdiFormatListBulleted} size={0.83} />
        </button>
      </div>
      <div className="toolbar-group">
        <button
          type="button"
          className={`toolbar-btn ${isLink ? "active" : ""}`}
          onClick={handleLink}
          title={isLink ? "Remove Link" : "Insert Link"}
        >
          <Icon path={mdiLink} size={0.83} />
        </button>
      </div>
    </div>
  );
};

export default ToolbarPlugin;
